import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Wallet, Plus, Trash2, ChevronDown, ChevronUp, Receipt } from "lucide-react";
import { managersFinance, managersExpenses, managersAddExpense, managersDeleteExpense } from "@/lib/afmApi";
import { Loading } from "@/components/common";

const errText = (e) => e?.response?.data?.detail || "No se pudo completar";
const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-black";
const money = (v, cur) => `${Number(v || 0).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;
const CAT_LABEL = { video: "Video", promocion: "Promoción", estudio: "Estudio", adelanto: "Adelanto", viaje: "Viaje", imagen: "Imagen / fotos", eventos: "Eventos", otros: "Otros" };

export const fmtMoney = money;
const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthLabel = (p) => { const [y, m] = p.split("-"); return new Date(+y, +m - 1, 1).toLocaleDateString("es-VE", { month: "long", year: "numeric" }); };
export function ranges() {
  const now = new Date();
  const back = (n) => ym(new Date(now.getFullYear(), now.getMonth() - n, 1));
  const out = [
    { key: "3", label: "Últimos 3 meses", desde: back(3), hasta: back(1) },
    { key: "6", label: "Últimos 6 meses", desde: back(6), hasta: back(1) },
    { key: "12", label: "Últimos 12 meses", desde: back(12), hasta: back(1) },
  ];
  for (let i = 1; i <= 12; i++) out.push({ key: `m${i}`, label: monthLabel(back(i)), desde: back(i), hasta: back(i) });
  return out;
}

// La compañía ve desde SU parte: el total y la parte de Always son solo de Always Fresh.
const COLS = [
  { k: "company", label: "Tu parte", cls: "text-black" },
  { k: "artist", label: "Artistas", cls: "text-[#AC3333]" },
  { k: "company_net", label: "Neto compañía", cls: "text-emerald-700 font-semibold" },
];
const OWN_API = { list: managersExpenses, add: managersAddExpense, remove: managersDeleteExpense };

export function Expenses({ artists, onChange, api = OWN_API, isLabel = false }) {
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({ categories: [], currencies: ["USD"] });
  const [filter, setFilter] = useState("");
  const [f, setF] = useState(null);
  const load = useCallback(() => {
    api.list(filter ? { artist_id: filter } : {}).then((d) => { setRows(d.expenses || []); setMeta(d); }).catch((e) => { setRows([]); toast.error(errText(e)); });
  }, [filter, api]);
  useEffect(() => { load(); }, [load]);
  const blank = () => ({ artist_id: filter || artists[0]?.id || "", date: new Date().toISOString().slice(0, 10), concept: "", category: "video", amount: "", currency: "USD", note: "" });
  const save = async () => {
    try { await api.add({ ...f, amount: Number(f.amount) }); toast.success("Gasto registrado"); setF(null); load(); onChange(); } catch (e) { toast.error(errText(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Borrar este gasto?")) return;
    try { await api.remove(id); load(); onChange(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="flex flex-1 items-center gap-2 text-sm font-semibold text-black"><Receipt className="h-4 w-4" /> Gastos en artistas <span className="font-normal text-slate-400">· solo control, no se descuentan</span></h3>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm">
          <option value="">Todos los artistas</option>
          {artists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        {!f && meta.can_edit && <button onClick={() => setF(blank())} className="flex items-center gap-1.5 rounded-lg bg-black px-3 py-1.5 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Registrar gasto</button>}
      </div>
      {f && (
        <div className="grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-3">
          <select value={f.artist_id} onChange={(e) => setF({ ...f, artist_id: e.target.value })} className={inputCls}>{artists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select>
          <input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inputCls} />
          <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={inputCls}>{(meta.categories || []).map((c) => <option key={c} value={c}>{CAT_LABEL[c] || c}</option>)}</select>
          <input value={f.concept} onChange={(e) => setF({ ...f, concept: e.target.value })} placeholder="Concepto (ej. Videoclip 'Tema X')" className={`${inputCls} sm:col-span-2`} maxLength={120} />
          <div className="flex gap-2">
            <input type="number" min="0" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="Monto" className={inputCls} />
            <select value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} className="rounded-lg border border-slate-300 bg-white px-2 text-sm">{(meta.currencies || ["USD"]).map((c) => <option key={c}>{c}</option>)}</select>
          </div>
          <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Nota (opcional)" className={`${inputCls} sm:col-span-3`} maxLength={300} />
          <div className="flex gap-2 sm:col-span-3">
            <button onClick={save} disabled={!f.concept.trim() || !(Number(f.amount) > 0)} className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">Guardar gasto</button>
            <button onClick={() => setF(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm">Cancelar</button>
          </div>
        </div>
      )}
      <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
        {rows === null && <p className="px-5 py-4 text-sm text-slate-400">Cargando…</p>}
        {(rows || []).slice(0, 200).map((e) => (
          <div key={e.id} className="flex flex-wrap items-center gap-3 px-5 py-2.5 text-sm">
            <span className="shrink-0 font-mono text-xs text-slate-500 sm:w-24">{e.date}</span>
            <div className="min-w-0 flex-1"><div className="font-medium text-black">{e.concept}</div><div className="text-xs text-slate-500">{e.artist_name} · {CAT_LABEL[e.category] || e.category}{e.note ? ` · ${e.note}` : ""}{e.by_label && <span className="ml-1.5 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">Always Fresh</span>}</div></div>
            <span className="whitespace-nowrap font-mono text-sm font-semibold">{money(e.amount, e.currency)}</span>
            {meta.can_edit && (isLabel || !e.by_label) && <button onClick={() => remove(e.id)} className="text-slate-400 hover:text-red-500" title="Borrar"><Trash2 className="h-3.5 w-3.5" /></button>}
          </div>
        ))}
        {rows && !rows.length && <p className="px-5 py-6 text-center text-sm text-slate-400">Aún no hay gastos registrados.</p>}
      </div>
    </section>
  );
}

// Finanzas del sello: regalías reales repartidas (Always → compañía → artista) y gastos por artista.
export default function ManagerFinance({ artists = [] }) {
  const opts = useMemo(ranges, []);
  const [range, setRange] = useState("3");
  const [d, setD] = useState(undefined);
  const [open, setOpen] = useState(null);
  const r = opts.find((o) => o.key === range) || opts[0];
  const load = useCallback(() => {
    setD(undefined);
    managersFinance(r.desde, r.hasta).then(setD).catch((e) => setD({ error: errText(e) }));
  }, [r.desde, r.hasta]);
  useEffect(() => { load(); }, [load]);

  const header = (
    <div className="flex flex-wrap items-center gap-3">
      <h2 className="flex flex-1 items-center gap-2 font-serif text-xl font-bold text-black"><Wallet className="h-5 w-5" /> {d?.role === "artist" ? "Tus ganancias" : "Finanzas del sello"}</h2>
      <select value={range} onChange={(e) => setRange(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm capitalize">
        {opts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
    </div>
  );
  const note = <p className="text-[11px] text-slate-500">Datos reales de la distribuidora. Los montos de un mes llegan cuando las tiendas reportan (normalmente 1 a 2 meses después). Cada moneda se muestra por separado.</p>;

  if (d === undefined) return <div className="space-y-4">{header}<Loading /></div>;
  if (d.error) return <div className="space-y-4">{header}<p className="rounded-xl border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500">{d.error}</p></div>;

  const empty = !d.in_stores;
  if (d.role === "artist") {
    const a = d.artists?.[0];
    const tot = Object.entries(a?.totals || {});
    return (
      <div className="space-y-4" data-testid="artist-earnings">
        {header}
        <div className="grid gap-3 sm:grid-cols-3">
          {tot.map(([cur, t]) => (
            <div key={cur} className="rounded-xl border border-slate-200 bg-white p-5"><div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Tu parte{a.artist_pct != null ? ` (${a.artist_pct}%)` : ""}</div><div className="mt-1 font-mono text-2xl font-bold text-black">{money(t.artist, cur)}</div></div>
          ))}
          {!tot.length && <p className="rounded-xl border border-dashed border-slate-300 px-5 py-6 text-sm text-slate-500 sm:col-span-3">{empty ? "Aún no tienes lanzamientos en tiendas." : "Aún no hay reportes para este período."}</p>}
        </div>
        {a?.releases?.some((x) => Object.keys(x.totals).length) && (
          <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
            {a.releases.filter((x) => Object.keys(x.totals).length).map((x) => (
              <div key={x.release_id} className="flex items-center gap-3 px-5 py-2.5 text-sm"><span className="flex-1">{x.title}</span>{Object.entries(x.totals).map(([c, v]) => <span key={c} className="font-mono">{money(v, c)}</span>)}</div>
            ))}
          </div>
        )}
        {note}
      </div>
    );
  }

  const totals = Object.entries(d.totals || {});
  return (
    <div className="space-y-6" data-testid="manager-finance">
      {header}
      {totals.map(([cur, t]) => (
        <div key={cur} className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {COLS.map((c) => (
            <div key={c.k} className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{c.label}</div><div className={`mt-1 font-mono text-lg tabular-nums ${c.cls}`}>{money(t[c.k], cur)}</div></div>
          ))}
          <div className="rounded-xl border border-slate-200 bg-white p-4"><div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Gastos</div><div className="mt-1 font-mono text-lg tabular-nums text-amber-700">{money(d.expenses_total?.[cur] || 0, cur)}</div></div>
        </div>
      ))}
      {!totals.length && <p className="rounded-xl border border-dashed border-slate-300 px-5 py-6 text-center text-sm text-slate-500">{empty ? "Todavía no hay lanzamientos en tiendas." : "Aún no hay reportes de regalías para este período."}</p>}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3 font-semibold">Artista</th>
              <th className="px-3 py-3 font-semibold">Reparto</th>
              {COLS.map((c) => <th key={c.k} className="px-3 py-3 text-right font-semibold">{c.label}</th>)}
              <th className="px-5 py-3 text-right font-semibold">Gastos</th>
            </tr>
          </thead>
          <tbody>
            {(d.artists || []).map((a) => {
              const curs = [...new Set([...Object.keys(a.totals || {}), ...Object.keys(a.expenses || {})])];
              const rowsFor = curs.length ? curs : [null];
              return (
                <Fragment key={a.id}>
                {rowsFor.map((cur, i) => (
                  <tr key={`${a.id}-${cur || "x"}`} className={i === 0 ? "border-t border-slate-100" : ""}>
                    <td className="px-5 py-2.5">{i === 0 && <button onClick={() => setOpen(open === a.id ? null : a.id)} className="flex items-center gap-1 font-medium text-black hover:underline">{a.name} {open === a.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}</button>}</td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-slate-500">{i === 0 && (a.artist_pct != null ? <>{a.artist_pct}% de tu parte</> : "Sin reparto")}</td>
                    {COLS.map((c) => <td key={c.k} className={`px-3 py-2.5 whitespace-nowrap text-right font-mono tabular-nums ${c.cls}`}>{cur && a.totals?.[cur] ? money(a.totals[cur][c.k], cur) : "—"}</td>)}
                    <td className="px-5 py-2.5 whitespace-nowrap text-right font-mono tabular-nums text-amber-700">{cur && a.expenses?.[cur] ? money(a.expenses[cur], cur) : "—"}</td>
                  </tr>
                ))}
                {open === a.id && (
                  <tr key={`${a.id}-rels`}><td colSpan={6} className="bg-slate-50 px-5 py-3">
                    {a.releases?.length ? a.releases.map((x) => (
                      <div key={x.release_id} className="flex items-center gap-3 py-1 text-xs"><span className="flex-1">{x.title}{!x.in_stores && <span className="text-slate-400"> · aún no está en tiendas</span>}</span>{Object.entries(x.totals).map(([c, v]) => <span key={c} className="font-mono">{money(v, c)}</span>)}{!Object.keys(x.totals).length && x.in_stores && <span className="text-slate-400">sin reporte</span>}</div>
                    )) : <span className="text-xs text-slate-400">Sin lanzamientos propios (las colaboraciones cuentan en el artista que saca el tema).</span>}
                  </td></tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {note}
      <Expenses artists={artists} onChange={load} />
    </div>
  );
}
